//  questions of current lesson
import { LessonAttributes, LessonCreationAttributes } from "common/lessonTypes";
import { defineStore } from "pinia";
import apiHelper from "../../helpers/apiHelper";
import { useUserStore } from "./userStore";
import { ref } from "vue";
import {
  StudentLessonAttributes,
  StudentLessonCreationAttributes,
} from "common/userTypes";
import useLessonTemplateHelper from "../../helpers/lessonTemplateHelper";

export const PENDING_CREATED_LESSON_KEY = "pendingCreatedLessonUUId";

export const useLessonStore = defineStore("lesson", () => {
  let lessons = ref<Map<String, LessonAttributes>>(new Map());
  let currentLesson = ref<LessonAttributes>();

  /** Lesson UUID we are navigating to after create; blocks list clicks until open completes. */
  const pendingOpenLessonUUId = ref<string | null>(
    sessionStorage.getItem(PENDING_CREATED_LESSON_KEY),
  );

  function getCurrentLesson() {
    return currentLesson.value;
  }

  function getLessons() {
    return lessons.value;
  }

  async function loadLesson(lessonUUId: string) {
    const db = apiHelper();
    let lesson = await db.getLesson(lessonUUId);

    if (!lesson) {
      throw new Error(`lesson ${lessonUUId} does not exists`);
    }

    lessons.value.set(lesson.uuid, lesson);
  }

  async function loadLessons() {
    clearLessons();
    const userStore = useUserStore();
    let userUUId = userStore.getCurrentUser()!.uuid;

    const db = apiHelper();
    if (userStore.isTeacher()) {
      const lessonsFromDB = await db.getTeacherLessons(userUUId);
      if (!lessonsFromDB) return;
      lessonsFromDB.forEach((l: LessonAttributes) => {
        lessons.value.set(l.uuid, l);
      });
    } else {
      const studentLessonsFromDB = await db.getStudentLessons(userUUId);
      if (!studentLessonsFromDB) return;
      studentLessonsFromDB.forEach((sl: StudentLessonAttributes) => {
        lessons.value.set(sl.lesson.uuid, sl.lesson);
      });
    }
  }

  async function setCurrentLesson(lessonUUId: string) {
    currentLesson.value = lessons.value.get(lessonUUId);
  }

  async function addLessonFromTemplate(
    templateId: string,
  ): Promise<LessonAttributes> {
    const userStore = useUserStore();
    const owner = userStore.getCurrentUser()!;
    const templateHelper = useLessonTemplateHelper();
    let createdLesson = await templateHelper.createLessonFromTemplate(
      templateId,
      owner,
    );
    if (!createdLesson.user?.uuid) {
      createdLesson = { ...createdLesson, user: owner };
    }
    lessons.value.set(createdLesson.uuid, createdLesson);
    currentLesson.value = createdLesson;
    return createdLesson;
  }

  async function addLesson(lessonName: string): Promise<LessonAttributes> {
    const userStore = useUserStore();
    const db = apiHelper();
    const owner = userStore.getCurrentUser()!;
    const lesson: LessonCreationAttributes = {
      name: lessonName,
      user: owner,
    };
    let createdLesson = await db.addLesson(lesson);
    if (!createdLesson.user?.uuid) {
      createdLesson = { ...createdLesson, user: owner };
    }
    lessons.value.set(createdLesson.uuid, createdLesson);
    currentLesson.value = createdLesson;
    return createdLesson;
  }

  async function addLessonToSharedLessons() {
    const userStore = useUserStore();
    const db = apiHelper();
    let studentLesson: StudentLessonCreationAttributes = {
      user: userStore.getCurrentUser()!,
      lesson: currentLesson.value!,
    };

    await db.addLessonToSharedLessons(studentLesson);
  }

  function removeLesson(lessonUUId: string) {
    lessons.value.delete(lessonUUId);
    if (currentLesson.value?.uuid === lessonUUId) {
      currentLesson.value = undefined;
    }
  }

  async function updateLesson(
    lessonUUId: string,
    lessonName: string,
  ): Promise<LessonAttributes> {
    const db = apiHelper();
    const updated = await db.updateLesson(lessonUUId, lessonName);
    lessons.value.set(updated.uuid, updated);
    if (currentLesson.value?.uuid === lessonUUId) {
      currentLesson.value = updated;
    }
    return updated;
  }

  async function deleteLesson(lessonUUId: string): Promise<void> {
    const db = apiHelper();
    await db.deleteLesson(lessonUUId);
    removeLesson(lessonUUId);
  }

  function clearLessons() {
    lessons.value.clear();
  }

  function beginOpeningLesson(lessonUUId: string) {
    pendingOpenLessonUUId.value = lessonUUId;
    sessionStorage.setItem(PENDING_CREATED_LESSON_KEY, lessonUUId);
  }

  function finishOpeningLesson() {
    pendingOpenLessonUUId.value = null;
    sessionStorage.removeItem(PENDING_CREATED_LESSON_KEY);
  }

  function getPendingOpenLessonUUId(): string | null {
    return (
      pendingOpenLessonUUId.value ??
      sessionStorage.getItem(PENDING_CREATED_LESSON_KEY)
    );
  }

  function isLessonListNavigationLocked(): boolean {
    return !!getPendingOpenLessonUUId();
  }

  return {
    getLessons,
    getCurrentLesson,
    loadLesson,
    loadLessons,
    setCurrentLesson,
    addLesson,
    addLessonFromTemplate,
    addLessonToSharedLessons,
    removeLesson,
    updateLesson,
    deleteLesson,
    clearLessons,
    beginOpeningLesson,
    finishOpeningLesson,
    getPendingOpenLessonUUId,
    isLessonListNavigationLocked,
  };
});
